import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, IconTile, ListRow, Money, SectionLabel, Stack } from '../../components';
import { ChevronRightIcon, UsersIcon } from '../../components/icons';
import { categoryIcon } from '../../components/icons';
import { formatDayHeading } from '../../lib/dates';
import { fromCents, toCents } from '../../lib/money';
import { useTrip } from './TripContext';

export function OverviewTab() {
  const { trip, expenses, balances, myMemberId, nameOf, openExpenseSheet } = useTrip();

  const totals = useMemo(() => {
    const totalCents = expenses.reduce((sum, e) => sum + toCents(e.base_amount), 0);
    const myShareCents = myMemberId
      ? expenses.reduce(
          (sum, e) =>
            sum +
            e.trip_expense_splits
              .filter((s) => s.member_id === myMemberId)
              .reduce((inner, s) => inner + toCents(s.base_amount), 0),
          0,
        )
      : 0;
    return { total: fromCents(totalCents), myShare: fromCents(myShareCents) };
  }, [expenses, myMemberId]);

  const myNet = Number(balances.find((b) => b.member_id === myMemberId)?.net_base ?? 0);
  const recent = expenses.slice(0, 5);

  return (
    <Stack gap={5}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
        <div>
          <Link to="/" style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-lg)' }}>
            {trip.name}
          </Link>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-display)', marginTop: 2 }}>
            {myMemberId ? nameOf(myMemberId) : 'You'}
          </div>
        </div>
        <Link to={`/trips/${trip.id}/group`} aria-label="Group">
          <IconTile size={46}>
            <UsersIcon size={22} />
          </IconTile>
        </Link>
      </header>

      {!myMemberId && (
        <Alert tone="warning" title="You don’t have a seat on this trip yet">
          You can see the trip, but nothing is being split with you. Add yourself under Group.
        </Alert>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
          <Money amount={myNet} currency={trip.base_currency} size={54} tone="auto" weight={300} />
          <span
            style={{
              fontSize: 'var(--fs-h3)',
              paddingTop: 8,
              color: myNet < -0.005 ? 'var(--error)' : myNet > 0.005 ? 'var(--accent)' : 'var(--text-muted)',
            }}
          >
            {myNet < -0.005 ? 'Owing' : myNet > 0.005 ? 'Owed' : 'Settled'}
          </span>
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-lg)', marginTop: 'var(--sp-2)' }}>
          {myNet < -0.005
            ? 'You owe the group'
            : myNet > 0.005
              ? 'The group owes you'
              : 'Nothing outstanding'}
        </div>
      </div>

      <Card>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, paddingRight: 'var(--sp-4)' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-md)' }}>Trip total</div>
            <div style={{ marginTop: 6 }}>
              <Money amount={totals.total} currency={trip.base_currency} size={26} decimals={0} weight={300} />
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)', marginTop: 4 }}>
              {expenses.length} expense{expenses.length === 1 ? '' : 's'}
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ flex: 1, paddingLeft: 'var(--sp-4)' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-md)' }}>Your share</div>
            <div style={{ marginTop: 6 }}>
              <Money amount={totals.myShare} currency={trip.base_currency} size={26} decimals={0} weight={300} />
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)', marginTop: 4 }}>
              {totals.total > 0 ? `${Math.round((totals.myShare / totals.total) * 100)}% of the trip` : '—'}
            </div>
          </div>
        </div>
      </Card>

      <div>
        <SectionLabel
          right={
            expenses.length > 0 ? (
              <Link to={`/trips/${trip.id}/expenses`} style={{ fontSize: 'var(--fs-body-sm)' }}>
                See all
              </Link>
            ) : undefined
          }
        >
          Recent
        </SectionLabel>

        {recent.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing logged yet"
              action={<Button onClick={() => openExpenseSheet()}>Add the first expense</Button>}
            >
              Add what you have spent so far and Tabi will work out who owes whom.
            </EmptyState>
          </Card>
        ) : (
          <Card flush>
            {recent.map((expense, index) => {
              const CategoryIcon = categoryIcon(expense.category);
              return (
                <ListRow
                  key={expense.id}
                  divider={index > 0}
                  onClick={() => openExpenseSheet(expense)}
                >
                  <IconTile size={40}>
                    <CategoryIcon size={19} />
                  </IconTile>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--fs-body-lg)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {expense.description}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
                      {nameOf(expense.payer_member_id)} · {formatDayHeading(expense.spent_at)}
                    </div>
                  </div>
                  <Money amount={expense.base_amount} currency={trip.base_currency} />
                  <ChevronRightIcon size={16} style={{ color: 'var(--text-muted)' }} />
                </ListRow>
              );
            })}
          </Card>
        )}
      </div>
    </Stack>
  );
}
